import React, { useRef, useEffect, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { themes } from '../themes';
import ReactDOM from 'react-dom';
import holdemhandranker from 'holdemhandranker';
import { AppContext } from '../contexts/AppContext';
import { useTranslation } from 'react-i18next';

// 辅助函数：首字母大写
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// 辅助函数：转换花色为符号
const convertSuitToSymbol = (suit) => {
  switch (suit.toLowerCase()) {
    case 's': return '♠️';
    case 'h': return '♥️';
    case 'd': return '♦️';
    case 'c': return '♣️';
    default: return suit;
  }
};

// 辅助函数：合并相同类型的连续行动
const mergeConsecutiveActions = (actions) => {
  if (!actions || actions.length <= 1) return actions;
  
  const result = [];
  let currentGroup = null;
  
  // 逐个处理行动，只合并连续的相同类型行动
  actions.forEach((action, index) => {
    if (!action) return;
    
    const actionType = action.action;
    const actionAmount = action.amount;
    
    // 检查是否可以合并到当前组
    // 只有fold、check或相同金额的call才能被合并
    const canMerge = 
      currentGroup && 
      currentGroup.action === actionType && 
      (actionType === 'fold' || actionType === 'check' || 
       (actionType === 'call' && currentGroup.amount === actionAmount));
    
    if (canMerge) {
      // 将当前行动合并到组中
      currentGroup.players.push(action.player);
      // 更新底池大小为最新的
      currentGroup.potSize = action.potSize;
    } else {
      // 保存之前的组（如果有）
      if (currentGroup) {
        // 如果只有一个玩家，使用原始格式
        if (currentGroup.players.length === 1) {
          result.push({
            player: currentGroup.players[0],
            action: currentGroup.action,
            amount: currentGroup.amount,
            potSize: currentGroup.potSize
          });
        } else {
          // 多个玩家，根据数量选择合并格式
          const firstPlayer = currentGroup.players[0];
          const lastPlayer = currentGroup.players[currentGroup.players.length - 1];
          
          // 如果是2个玩家，使用逗号连接；超过2个玩家，使用省略号
          const mergedPlayerName = currentGroup.players.length === 2 
            ? `${firstPlayer}, ${lastPlayer}` 
            : `${firstPlayer}...${lastPlayer}`;
          
          result.push({
            players: currentGroup.players,
            action: currentGroup.action,
            amount: currentGroup.amount,
            potSize: currentGroup.potSize,
            player: mergedPlayerName
          });
        }
      }
      
      // 创建新组
      currentGroup = {
        players: [action.player],
        action: actionType,
        amount: actionAmount,
        potSize: action.potSize
      };
    }
  });
  
  // 不要忘记添加最后一组
  if (currentGroup) {
    if (currentGroup.players.length === 1) {
      result.push({
        player: currentGroup.players[0],
        action: currentGroup.action,
        amount: currentGroup.amount,
        potSize: currentGroup.potSize
      });
    } else {
      // 多个玩家，根据数量选择合并格式
      const firstPlayer = currentGroup.players[0];
      const lastPlayer = currentGroup.players[currentGroup.players.length - 1];
      
      // 如果是2个玩家，使用逗号连接；超过2个玩家，使用省略号
      const mergedPlayerName = currentGroup.players.length === 2 
        ? `${firstPlayer}, ${lastPlayer}` 
        : `${firstPlayer}...${lastPlayer}`;
      
      result.push({
        players: currentGroup.players,
        action: currentGroup.action,
        amount: currentGroup.amount,
        potSize: currentGroup.potSize,
        player: mergedPlayerName
      });
    }
  }
  
  return result;
};

// 辅助函数：计算内容高度
const calculateContentHeight = (actions, gameInfo, config) => {
  let height = config.margin;

  // 标题和副标题高度
  height += config.titleSize;
  height += config.subtitleSize + 5;
  height += config.margin / 2;  // 减少顶部边距
  
  // 英雄信息高度
  height += config.lineHeight;
  height += config.margin / 2;  // 减少边距
  
  // 计算每个街道的高度
  Object.entries(actions).forEach(([street, streetActions]) => {
    if (!streetActions || streetActions.length === 0) return;
    
    // 街道标题高度
    height += config.streetHeaderHeight;
    height += config.streetSpacing / 2;  // 减少间距
    
    // 合并相同类型的连续行动
    const mergedActions = mergeConsecutiveActions(streetActions);
    
    // 每个动作的高度
    mergedActions.forEach(() => {
      height += config.actionHeight;
      height += config.actionSpacing / 2;  // 减少间距
    });
  });

  // Showdown 信息的高度
  if (gameInfo.showdown) {
    height += config.streetHeaderHeight;
    height += config.streetSpacing / 2;
    const showdownCount = Object.keys(gameInfo.showdown).length;
    height += (config.actionHeight + config.actionSpacing / 2) * showdownCount;
  }

  // 底部信息高度
  if (Object.values(actions).flat().length > 0) {
    height += config.margin / 2;  // 减少底部边距
    height += config.lineHeight * 1.5;  // 减少底部信息高度
  } else {
    height += config.lineHeight;
  }

  // 添加推广文案所需的高度
  height += config.margin * 2;  // 分隔线上下的间距
  height += config.lineHeight * 2;  // 两行文本的高度
  height += config.margin;  // 底部额外间距

  return height;
};

// 帮助函数：绘制圆角矩形
const roundRect = (ctx, x, y, width, height, radius, fill, stroke) => {
  if (!ctx) return;
  
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  if (fill) {
    ctx.fill();
  }
  if (stroke) {
    ctx.stroke();
  }
};

// 辅助函数：格式化动作文本
const formatActionText = (action) => {
  if (!action) return '';
  
  const player = action.player || '';
  const actionType = action.action || '';
  const amount = action.amount;
  
  switch (actionType.toLowerCase()) {
    case 'fold':
      return `${player} Folds`;
    case 'check':
      return `${player} Checks`;
    case 'call':
      return `${player} Calls ${amount}`;
    case 'bet':
      return `${player} Bets ${amount}`;
    case 'raise':
      return `${player} Raises to ${amount}`;
    case 'limp':
      return `${player} Limps`;
    case 'open':
      return `${player} Opens ${amount}`;
    case 'wins':
      return `${player} Wins the pot`;
    default:
      // 确保其他动作也是首字母大写
      const capitalizedAction = actionType.charAt(0).toUpperCase() + actionType.slice(1).toLowerCase();
      return `${player} ${capitalizedAction} ${amount || ''}`.trim();
  }
};

// 修改渲染内容的函数 - 移到组件外部
const renderContent = (ctx, actions, gameInfo, heroInfo, boardCards, config, theme, cardImages, t) => {
  let y = config.margin;
  
  // 设置标题样式
  ctx.fillStyle = theme.textColor;
  ctx.font = `${theme.titleFontWeight} ${config.titleSize}px ${theme.fontFamily}`;
  ctx.textAlign = 'left';
  ctx.fillText(`${gameInfo.stakes || 'Unknown'} ${gameInfo.gameType || 'Game'}`, config.margin, y += config.titleSize);
  
  // 设置副标题样式
  ctx.font = `${theme.textFontWeight} ${config.subtitleSize}px ${theme.fontFamily}`;
  ctx.fillText(`${gameInfo.players || '?'} handed`, config.margin, y += config.subtitleSize + 5);
  
  y += config.margin / 2;
  
  // 设置英雄信息样式
  ctx.font = `${theme.textFontWeight} ${config.textSize}px ${theme.fontFamily}`;
  const heroTextY = y + config.lineHeight/2 + config.textSize/3 - 1;
  ctx.fillText(`Hero`, config.margin, heroTextY);
  ctx.fillText(`(${heroInfo.position || '?'})`, config.margin + config.heroPositionOffset, heroTextY);
  
  // 绘制英雄手牌
  if (heroInfo.hand && heroInfo.hand.length > 0) {
    let cardX = config.margin + config.heroCardOffset;
    heroInfo.hand.forEach(card => {
      if (card && cardImages[card]) {
        ctx.drawImage(cardImages[card], cardX, y + config.lineHeight/2 - config.cardHeight/2, 
          config.cardWidth, config.cardHeight);
        cardX += config.cardWidth + config.heroCardSpacing;
      }
    });
  }
  
  y += config.lineHeight + config.margin / 2;
  
  // 绘制每个街道的动作
  Object.entries(actions).forEach(([street, streetActions]) => {
    if (!streetActions || streetActions.length === 0) return;
    
    // 绘制街道标题背景
    ctx.fillStyle = theme.streetBgColor;
    roundRect(ctx, config.margin, y, config.width - config.margin * 2, config.streetHeaderHeight, 
      config.borderRadius / 2, true, false);
    
    // 设置街道标题样式
    ctx.fillStyle = theme.streetTextColor;
    ctx.font = `${theme.titleFontWeight} ${config.headerSize}px ${theme.fontFamily}`;
    ctx.textAlign = 'left';
    
    // 调整街道标题位置
    const streetTitleY = y + (config.streetHeaderHeight + config.headerSize) / 2 - 2;
    ctx.fillText(capitalizeFirst(street), config.margin + config.padding, streetTitleY);
    
    // 绘制公共牌
    if (street !== 'preflop' && boardCards[street] && boardCards[street].length > 0) {
      let cardX = config.width / 2;
      const cardSpacing = street === 'flop' ? 8 : 10;
      const totalCardsWidth = boardCards[street].filter(c => c).length * (config.cardWidth + cardSpacing) - cardSpacing;
      cardX -= totalCardsWidth / 2;
      
      boardCards[street].forEach(card => {
        if (card && cardImages[card]) {
          const boardCardY = y + (config.streetHeaderHeight - config.cardHeight) / 2;
          ctx.drawImage(cardImages[card], cardX, boardCardY, config.cardWidth, config.cardHeight);
          cardX += config.cardWidth + cardSpacing;
        }
      });
    }
    
    y += config.streetHeaderHeight + config.streetSpacing / 2;
    
    // 合并相同类型的连续行动
    const mergedActions = mergeConsecutiveActions(streetActions);
    
    // 绘制每个动作
    mergedActions.forEach(action => {
      // 绘制动作背景
      ctx.fillStyle = theme.actionBgColor;
      roundRect(ctx, config.margin, y, config.width - config.margin * 2, config.actionHeight, 
        config.borderRadius / 2);
      ctx.fill();
      
      // 设置动作文本样式
      ctx.fillStyle = theme.actionTextColor;
      ctx.font = `${theme.textFontWeight} ${config.textSize}px ${theme.fontFamily}`;
      const actionTextY = y + config.actionHeight/2 + config.textSize/3 - config.actionTextOffset;
      
      // 格式化并绘制动作文本
      const actionText = formatActionText(action);
      ctx.textAlign = 'left';
      ctx.fillText(actionText, config.margin + config.padding, actionTextY);
      
      // 绘制底池大小
      if (action.potSize) {
        ctx.textAlign = 'right';
        ctx.fillText(`Pot: ${action.potSize}`, config.width - config.margin - config.padding, actionTextY);
      }
      
      y += config.actionHeight + config.actionSpacing / 2;
    });
  });

  // 添加 Showdown 信息
  if (gameInfo.showdown) {
    // 绘制 Showdown 标题背景
    ctx.fillStyle = theme.streetBgColor;
    roundRect(ctx, config.margin, y, config.width - config.margin * 2, config.streetHeaderHeight, 
      config.borderRadius / 2, true, false);
    
    // 设置标题样式
    ctx.fillStyle = theme.streetTextColor;
    ctx.font = `${theme.titleFontWeight} ${config.headerSize}px ${theme.fontFamily}`;
    ctx.textAlign = 'left';
    
    // 绘制 Showdown 标题
    const showdownTitleY = y + (config.streetHeaderHeight + config.headerSize) / 2 - 2;
    ctx.fillText('Showdown', config.margin + config.padding, showdownTitleY);
    
    y += config.streetHeaderHeight + config.streetSpacing / 2;
    
    // 绘制每个玩家的手牌
    Object.entries(gameInfo.showdown).forEach(([player, cards]) => {
      // 绘制动作背景
      ctx.fillStyle = theme.actionBgColor;
      roundRect(ctx, config.margin, y, config.width - config.margin * 2, config.actionHeight, 
        config.borderRadius / 2);
      ctx.fill();
      
      // 设置玩家文本样式
      ctx.fillStyle = theme.actionTextColor;
      ctx.font = `${theme.textFontWeight} ${config.textSize}px ${theme.fontFamily}`;
      const textY = y + config.actionHeight/2 + config.textSize/3 - config.actionTextOffset;
      
      // 绘制玩家名称和手牌
      ctx.textAlign = 'left';
      // 将手牌数组转换为字符串，并添加花色符号
      const formattedCards = Array.isArray(cards) ? 
        cards.map(card => {
          const rank = card.slice(0, -1);
          const suit = card.slice(-1);
          return `${rank}${convertSuitToSymbol(suit)}`;
        }).join('') : 
        cards; // 如果已经是格式化的字符串，直接使用
      ctx.fillText(`${player} Shows ${formattedCards}`, config.margin + config.padding, textY);
      
      y += config.actionHeight + config.actionSpacing / 2;
    });
  }

  // 绘制底部信息
  if (Object.values(actions).flat().length > 0) {
    y += config.margin / 2;
    ctx.fillStyle = theme.textColor;
    ctx.font = `${theme.textFontWeight} ${config.textSize}px ${theme.fontFamily}`;
    ctx.textAlign = 'left';

    // 获取最后一个动作的底池大小
    const allActions = Object.values(actions).flat();
    const lastAction = allActions[allActions.length - 1];
    const finalPotSize = lastAction?.potSize || gameInfo.finalPot || '?';
    
    ctx.fillText(`Final pot size: ${finalPotSize}`, config.margin, y += config.lineHeight);
    
    // 显示获胜者信息
    if (gameInfo.winner) {
      // 如果是 showdown 模式，显示获胜者的牌型和牌
      if (gameInfo.showdown) {
        // 分割获胜者名单
        const winners = gameInfo.winner.split(', ');
        const winnerTexts = winners.map(winner => {
          const winnerCards = gameInfo.showdown[winner];
          if (!winnerCards) return null;

          const handValue = holdemhandranker.valueOfHand(
            [
              ...(boardCards.flop || []),
              ...(boardCards.turn ? [boardCards.turn[0]] : []),
              ...(boardCards.river ? [boardCards.river[0]] : [])
            ].filter(card => card !== ''),
            winnerCards
          );
          if (handValue) {
            // 第一行：获胜者和底池信息
            const winnerLine = `${winner} ${winners.length > 1 ? 'chops' : 'wins'} the pot (${finalPotSize})`;
            // 第二行：牌型和具体牌
            const handLine = `${handValue.handType} - ${handValue.cards.map(card => {
              const rank = card.slice(0, -1);
              const suit = card.slice(-1);
              return `${rank}${convertSuitToSymbol(suit)}`;
            }).join(' ')}`;
            
            // 分别绘制两行文本
            ctx.fillText(winnerLine, config.margin, y += config.lineHeight);
            ctx.fillText(handLine, config.margin + 20, y += config.lineHeight);
            y += config.lineHeight / 2; // 为下一个获胜者添加一些额外空间
            return null; // 不返回文本，因为我们已经直接绘制了
          }
          return null;
        });
      } else {
        const winnerText = `${gameInfo.winner} Wins the pot (${finalPotSize})`;
        ctx.fillText(winnerText, config.margin, y += config.lineHeight);
      }
    }
  } else {
    y += config.lineHeight;
  }

  // 在函数内部获取 hostname 和 appName
  const hostname = window.location.hostname;
  const appName = hostname.includes('fupan.72plus.pro') ? '复盘 PRO' : 'HAND PRO';

  // 添加推广文案
  y += config.margin * 2;
  
  // 绘制分隔线
  ctx.beginPath();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 1;
  ctx.moveTo(config.margin, y);
  ctx.lineTo(config.width - config.margin, y);
  ctx.stroke();
  
  y += config.margin;
  
  // 使用动态的应用名称和国际化文案
  ctx.fillStyle = theme.textColor;  // 使用主题定义的文本颜色
  ctx.font = `${theme.titleFontWeight} ${Math.floor(config.textSize * 1.2)}px ${theme.fontFamily}`;
  ctx.textAlign = 'center';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 1;
  
  // 使用国际化文本
  ctx.fillText(t('share.mainText', { appName }), config.width / 2, y);
  y += config.lineHeight * 1;  // 稍微增加与域名的间距
  
  // 设置域名样式 - 调整字体大小为更适中的尺寸
  ctx.shadowBlur = 0;  // 移除阴影
  ctx.font = `600 ${Math.floor(config.textSize * 1.7)}px ${theme.fontFamily}`;  // 调整字体尺寸为 1.8 倍
  
  // 为域名选择合适的颜色
  switch (theme.name) {
    case 'Pink to Blue':
      ctx.fillStyle = '#FFFFFF';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 3;  // 略微增加阴影模糊度
      break;
    case 'Blue to Purple':
      ctx.fillStyle = '#80FFDB';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
      ctx.shadowBlur = 3;
      break;
    case 'Blue to Pink':
      ctx.fillStyle = '#00FFF0';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
      ctx.shadowBlur = 3;
      break;
    case 'Purple to Blue':
      ctx.fillStyle = '#7AFFF7';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
      ctx.shadowBlur = 3;
      break;
    case 'Teal to Purple':
      ctx.fillStyle = '#FFFFFF';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 3;
      break;
    case 'Dark Blue':
      ctx.fillStyle = '#64FFDA';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 3;
      break;
    default:
      ctx.fillStyle = '#FFFFFF';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 3;
  }
  
  // 添加轻微发光效果
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  
  // 渲染域名
  ctx.fillText(t('share.domain', { domain: hostname }), config.width / 2, y);
  
  // 重置阴影
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
};

/**
 * 使用Canvas API渲染扑克手牌历史记录卡片
 * @param {Object} props - 组件属性
 * @param {Object} props.actions - 按街道组织的动作
 * @param {Object} props.gameInfo - 游戏信息
 * @param {Object} props.heroInfo - 英雄信息
 * @param {Object} props.boardCards - 按街道组织的公共牌
 * @param {boolean} props.isMobile - 是否为移动设备
 * @param {Function} props.onExport - 导出回调
 * @param {string} props.className - 额外的CSS类
 * @param {Object} props.theme - 主题配置
 */
const CanvasCardRenderer = forwardRef(({
  actions,
  gameInfo,
  heroInfo,
  boardCards,
  isMobile = false,
  onExport,
  className = '',
  theme: initialTheme = themes.pinkBlue,
  t
}, ref) => {
  const canvasRef = useRef(null);
  const [isRendering, setIsRendering] = useState(true);
  const [cardImages, setCardImages] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const [renderInitiated, setRenderInitiated] = useState(false);
  const [componentMounted, setComponentMounted] = useState(false);
  const [renderCounter, setRenderCounter] = useState(0); // 添加渲染计数器
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const [isOpen, setIsOpen] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Expose getCurrentTheme method through ref
  useImperativeHandle(ref, () => ({
    getCurrentTheme: () => currentTheme
  }));

  // 简单的调试函数
  const logState = (msg) => {
    // 注释掉调试信息
    /*
    console.log(`[Canvas] ${msg}`, {
      componentMounted,
      canvasReady,
      canvasExists: canvasRef.current !== null,
      renderInitiated,
      isRendering,
      cardsLoaded: Object.keys(cardImages).length,
      error: error || 'None',
      renderCounter
    });
    */
  };
  
  // 组件挂载检测
  useEffect(() => {
    console.log('CanvasCardRenderer 组件已挂载');
    setComponentMounted(true);
    
    // 检查是否已有Canvas元素
    if (canvasRef.current) {
      console.log('组件挂载时已检测到Canvas元素');
      setCanvasReady(true);
    }
    
    return () => {
      console.log('CanvasCardRenderer 组件卸载');
      setComponentMounted(false);
    };
  }, []);
  
  // 初始数据验证
  useEffect(() => {
    if (!componentMounted) return; // 确保组件已挂载
    
    logState('验证数据');
    
    if (!actions || !gameInfo || !heroInfo || !boardCards) {
      console.error('CanvasCardRenderer: 缺少必要数据', { actions, gameInfo, heroInfo, boardCards });
      setError('缺少必要数据，无法渲染卡片');
      setIsRendering(false);
      return;
    }
    
    if (!heroInfo.hand || heroInfo.hand.length === 0) {
      console.warn('警告: 英雄手牌未设置');
    }
    
    // 重置渲染状态
    setIsRendering(true);
    setRenderInitiated(true);
    
    // 手动检查是否已有Canvas元素
    if (canvasRef.current && !canvasReady) {
      console.log('在数据验证时检测到Canvas元素');
      setCanvasReady(true);
    }
    
  }, [actions, gameInfo, heroInfo, boardCards, componentMounted]);
  
  // 卡片尺寸配置 - 使用 useMemo 缓存
  const config = useMemo(() => ({
    width: 400,
    minHeight: 800,
    dynamicHeight: true,
    margin: 20,
    lineHeight: 24,
    titleSize: 18,
    subtitleSize: 14,
    headerSize: 16,
    textSize: 14,
    cardWidth: 25,
    cardHeight: 35,
    padding: 12,
    streetHeaderHeight: 44,
    streetTitleOffset: 2,
    actionHeight: 36,
    actionTextOffset: 1,
    streetSpacing: 8,
    actionSpacing: 5,
    bgColor: currentTheme.background || '#152238',
    textColor: currentTheme.textColor || '#ffffff',
    streetBgColor: currentTheme.streetBgColor || '#284985',
    actionBgColor: currentTheme.actionBgColor || '#1c3366',
    borderRadius: currentTheme.borderRadius || 10,
    heroCardSpacing: 8,
    heroPositionOffset: 45,
    heroCardOffset: 150,
    extraPadding: 50,
    streetPadding: 12,
    streetTitleWidth: 100,
  }), [currentTheme]); // 添加 theme 作为依赖
  
  // 添加一个专门的效果来检查和更新Canvas引用状态
  useEffect(() => {
    if (!componentMounted) return;
    
    // 使用requestAnimationFrame确保DOM已更新
    const checkCanvasRef = () => {
      if (canvasRef.current) {
        logState('检测到Canvas元素');
        setCanvasReady(true);
      } else {
        logState('Canvas元素不存在');
        // 仅当我们确定Canvas应该存在但不存在时才设置为false
        if (renderCounter > 2 && canvasReady) {
          setCanvasReady(false);
        }
      }
    };
    
    // 使用RAF延迟检查，确保DOM已更新
    const timer = requestAnimationFrame(checkCanvasRef);
    return () => cancelAnimationFrame(timer);
  }, [componentMounted, renderCounter]);
  
  // 每500ms强制重新检查一次Canvas状态
  useEffect(() => {
    if (!componentMounted) return;
    
    const interval = setInterval(() => {
      setRenderCounter(prev => prev + 1);
    }, 500);
    
    return () => clearInterval(interval);
  }, [componentMounted]);
  
  // 预加载卡片图片
  useEffect(() => {
    if (error || !renderInitiated || !componentMounted) return; // 如果有错误或还未初始化或组件未挂载，不加载图片
    
    console.log('开始加载卡片图片...');
    
    const loadCardImage = async (card) => {
      if (!card || card === '') return null;
      
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ card, img });
        img.onerror = (e) => {
          console.error(`Failed to load card image: ${card}`, e);
          resolve({ card, img: null });
        };
        
        // 使用绝对路径确保在任何环境中都能正确加载
        const baseUrl = window.location.origin;
        const imgUrl = `${baseUrl}/images/cards/${card}_borderless.svg`;
        console.log(`加载图片: ${imgUrl}`);
        img.src = imgUrl;
      });
    };
    
    const loadAllCards = async () => {
      console.log('加载卡片图片:', { heroInfo, boardCards });
      const cards = [
        ...(heroInfo?.hand || []), 
        ...(boardCards?.flop || []), 
        ...(boardCards?.turn || []), 
        ...(boardCards?.river || [])
      ].filter(card => card && card !== '');
      
      if (cards.length === 0) {
        console.warn('没有卡片需要加载');
        setIsRendering(false);
        return;
      }
      
      console.log('需要加载的卡片:', cards);
      
      try {
        const results = await Promise.all(cards.map(loadCardImage));
        
        const imageMap = {};
        let loadedCount = 0;
        results.forEach(result => {
          if (result && result.img) {
            imageMap[result.card] = result.img;
            loadedCount++;
          }
        });
        
        console.log(`加载完成的卡片图片: ${loadedCount}/${cards.length}`, Object.keys(imageMap));
        
        if (loadedCount === 0 && cards.length > 0) {
          setError('所有卡片图片加载失败');
          setIsRendering(false);
        } else {
          setCardImages(imageMap);
          // 保持 isRendering 为 true，等待渲染逻辑执行
        }
      } catch (e) {
        console.error('加载卡片图片时出错:', e);
        setError(`加载卡片图片失败: ${e.message}`);
        setIsRendering(false);
      }
    };
    
    loadAllCards();
  }, [heroInfo, boardCards, error, renderInitiated, componentMounted]);
  
  useEffect(() => {
    if (!componentMounted) {
      logState('组件尚未挂载，跳过渲染');
      return;
    }
    
    const canvas = canvasRef.current;
    
    if (!canvas) {
      logState('Canvas元素不存在，跳过渲染');
      return;
    }
    
    if (!renderInitiated || Object.keys(cardImages).length === 0) {
      logState('其他渲染条件未满足');
      return;
    }
    
    logState('开始渲染Canvas');
    
    try {
      const ctx = canvas.getContext('2d');
      
      canvas.width = config.width;
      let contentHeight = config.minHeight;
      
      let totalHeight = calculateContentHeight(actions, gameInfo, config);
      
      contentHeight = Math.max(contentHeight, totalHeight + config.extraPadding);
      
      canvas.height = contentHeight;
      
      ctx.clearRect(0, 0, config.width, contentHeight);
      
      if (currentTheme.background.includes('gradient')) {
        const gradient = ctx.createLinearGradient(0, 0, config.width, contentHeight);
        const colors = currentTheme.background.match(/hsla\([^)]+\)/g);
        if (colors) {
          colors.forEach((color, index) => {
            gradient.addColorStop(index / (colors.length - 1), color);
          });
        }
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = currentTheme.background;
      }
      
      roundRect(ctx, 0, 0, config.width, contentHeight, config.borderRadius, true, false);
      
      renderContent(ctx, actions, gameInfo, heroInfo, boardCards, config, currentTheme, cardImages, t);
      
      console.log('Canvas渲染完成, 高度:', contentHeight);
    } catch (e) {
      console.error('渲染Canvas时出错:', e);
      setError(`渲染失败: ${e.message}`);
    } finally {
      setIsRendering(false);
    }
  }, [canvasReady, cardImages, actions, gameInfo, heroInfo, boardCards, config, renderInitiated, componentMounted, currentTheme, t]);

  // 处理主题变更
  const handleThemeChange = (newTheme) => {
    setCurrentTheme(newTheme);
    // 重置渲染状态以触发重新渲染
    setRenderInitiated(true);
    setIsRendering(true);
  };

  // 计算最佳缩放比例
  const calculateScale = () => {
    if (!containerRef.current || !imageSize.height) return 0.95;

    const containerHeight = containerRef.current.clientHeight;
    const containerWidth = containerRef.current.clientWidth;
    
    // 计算基于高度和宽度的缩放比例
    const scaleByHeight = (containerHeight - 28) / imageSize.height;  // 减去上下边距
    const scaleByWidth = (containerWidth - 20) / imageSize.width;    // 减去左右边距
    
    // 选择较小的缩放比例以确保图片完全显示
    let scale = Math.min(scaleByHeight, scaleByWidth);
    
    // 额外缩小2%以确保有一些边距
    scale = Math.min(scale * 0.98, 1);
    
    return scale;
  };

  // 在渲染完成后更新图片尺寸
  useEffect(() => {
    if (!canvasRef.current || isRendering) return;

    const canvas = canvasRef.current;
    setImageSize({
      width: canvas.width,
      height: canvas.height
    });
  }, [isRendering]);

  // 监听容器尺寸变化
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      // 强制重新计算缩放比例
      setRenderCounter(prev => prev + 1);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`canvas-card-generator ${className}`} style={{
      position: 'relative',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0
    }}>
      {/* 标题栏 */}
      <div style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: '0 2px',
        backgroundColor: 'transparent',
        height: '8px',  // 从10px减小到8px
        position: 'relative',
        borderBottom: '1px solid rgba(255, 255, 255, 0.03)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1px',
          flex: 1,
          height: '100%'
        }}>
          <button style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '7px',  // 从8px减小到7px
            padding: '0 1px',
            cursor: 'pointer',
            height: '6px',  // 从8px减小到6px
            display: 'flex',
            alignItems: 'center',
            gap: '1px'
          }}>
            <svg width="4" height="4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
            文本模式
          </button>
        </div>
        
        <button style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255, 255, 255, 0.5)',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '6px',  // 从8px减小到6px
          width: '6px'  // 从8px减小到6px
        }}
        onClick={() => onExport && onExport()}
        >
          <svg width="4" height="4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* 分享菜单 */}
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        padding: 0,
        height: '6px',  // 从8px减小到6px
        borderBottom: '1px solid rgba(255, 255, 255, 0.03)'
      }}>
        <div style={{
          display: 'flex',
          gap: 0,
          height: '100%',
          background: 'transparent'
        }}>
          <button style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '7px',  // 从8px减小到7px
            padding: '0 1px',  // 从2px减小到1px
            height: '6px',  // 从8px减小到6px
            display: 'flex',
            alignItems: 'center',
            gap: '1px'
          }}>
            <svg width="4" height="4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            文本模式
          </button>
        </div>
      </div>
      
      {/* 画布容器 */}
      <div 
        ref={containerRef}
        className={`canvas-container ${className.includes('scale-preview') ? 'preview-mode' : ''}`} 
        style={{ 
          width: '100%', 
          height: 'calc(100vh - 14px)',  // 减小顶部空间
          margin: 0,
          overflow: 'hidden',
          position: 'relative',
          borderRadius: 0,
          boxShadow: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: 0,
          backgroundColor: 'transparent',
          left: isMobile ?'-14px' : '0px',
          top: isMobile ? '-6px' : '0px'
        }}
      >
        {/* 主题选择器 - 桌面和移动端都显示 */}
        <div style={{
          position: 'absolute',
          right: isMobile ? '0px' : '8px',  // 更靠右
          top: isMobile ? '8px' : '8px',    // 更靠上
          zIndex: 30,
          display: 'flex',
          alignItems: 'center',
          gap: '2px',                       // 减小间距
          padding: '2px',                   // 减小内边距
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '4px',
          backdropFilter: 'blur(8px)'       // 添加模糊效果
        }}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            style={{
              background: currentTheme.background,
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '3px',
              width: isMobile ? '20px' : '24px',    // 减小按钮尺寸
              height: isMobile ? '20px' : '24px',   // 减小按钮尺寸
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)'
            }}
          >
            <span style={{
              position: 'absolute',
              bottom: '-1px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '6px',                         // 减小指示器尺寸
              height: '2px',                        // 减小指示器尺寸
              borderLeft: '3px solid rgba(255, 255, 255, 0.5)',
              borderRight: '3px solid rgba(255, 255, 255, 0.5)'
            }} />
          </button>

          {/* 向下箭头图标 */}
          <svg 
            width="10" 
            height="10" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="rgba(255, 255, 255, 0.8)" 
            strokeWidth="2"
            style={{
              cursor: 'pointer'
            }}
            onClick={() => setIsOpen(!isOpen)}
          >
            <path d="M6 9l6 6 6-6"/>
          </svg>
          
          {isOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '0',
              marginTop: '4px',
              background: 'rgba(0, 0, 0, 0.8)',
              borderRadius: '3px',
              padding: '2px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '2px',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              zIndex: 31
            }}>
              {Object.entries(themes).map(([key, theme]) => (
                <button
                  key={key}
                  onClick={() => {
                    handleThemeChange(theme);
                    setIsOpen(false);
                  }}
                  style={{
                    width: isMobile ? '18px' : '20px',     // 减小主题按钮尺寸
                    height: isMobile ? '18px' : '20px',    // 减小主题按钮尺寸
                    border: currentTheme === theme ? '1px solid #4A90E2' : '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '2px',
                    cursor: 'pointer',
                    background: theme.background,
                    padding: 0,
                    transition: 'transform 0.2s ease',
                    ':hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                  title={key}
                />
              ))}
            </div>
          )}
        </div>

        <canvas 
          ref={canvasRef}
          id="pokerCanvas"
          className={`poker-card-canvas rounded-lg shadow-lg ${className.includes('scale-preview') ? 'preview-canvas' : ''}`}
          width="400" 
          height="800"
          style={{ 
            width: 'auto',
            height: 'auto',
            maxHeight: '100%',
            maxWidth: '100%',
            opacity: (!isRendering && !error) ? 1 : 0,
            position: 'relative',
            zIndex: (!isRendering && !error) ? 10 : -1,
            transform: className.includes('scale-preview') ? `scale(${calculateScale()})` : 'none',
            transformOrigin: 'top center',
            display: 'block',
            margin: '7px 0',  // 恢复原来的边距设置
            objectFit: 'contain',
            transition: 'all 0.15s ease'
          }}
        />
        
        {/* 状态提示信息 */}
        {!componentMounted && (
          <div className="absolute inset-0 flex justify-center items-center" style={{
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '2px'
          }}>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px' }}>初始化中...</p>
          </div>
        )}
        
        {componentMounted && isRendering && (
          <div className="absolute inset-0 flex justify-center items-center" style={{
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '2px'
          }}>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px' }}>生成中...</p>
          </div>
        )}
        
        {componentMounted && !isRendering && error && (
          <div className="absolute inset-0 flex justify-center items-center" style={{
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '2px'
          }}>
            <p style={{ color: '#ff4d4f', textAlign: 'center', fontSize: '10px' }}>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * 使用Canvas生成图片
 */
export const generateCanvasImage = async (options) => {
  const { actions, gameInfo, heroInfo, boardCards, isMobile = false, theme = themes.pinkBlue, t } = options;
  
  if (!actions || !gameInfo || !heroInfo || !boardCards) {
    throw new Error('缺少必要数据，无法生成图片');
  }

  if (!t || typeof t !== 'function') {
    throw new Error('缺少翻译函数，无法生成图片');
  }
  
  // 创建临时Canvas
  const canvas = document.createElement('canvas');
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.appendChild(canvas);
  document.body.appendChild(container);
  
  try {
    // 加载卡片图片
    const cardImages = {};
    const cards = [
      ...(heroInfo.hand || []), 
      ...(boardCards.flop || []), 
      ...(boardCards.turn || []), 
      ...(boardCards.river || [])
    ].filter(card => card && card !== '');
    
    await Promise.all(cards.map(async (card) => {
      if (!card) return;
      
      const img = new Image();
      await new Promise((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        const baseUrl = window.location.origin;
        img.src = `${baseUrl}/images/cards/${card}_borderless.svg`;
      });
      
      cardImages[card] = img;
    }));
    
    // 渲染配置 - 导出时使用更高质量的设置
    const config = {
      width: 800,
      minHeight: 1600,
      dynamicHeight: true,
      margin: 40,
      lineHeight: 48,
      titleSize: 36,
      subtitleSize: 28,
      headerSize: 32,
      textSize: 28,
      cardWidth: 50,
      cardHeight: 70,
      padding: 24,
      streetHeaderHeight: 88,
      streetTitleOffset: 4,
      actionHeight: 72,
      actionTextOffset: 2,
      streetSpacing: 16,
      actionSpacing: 10,
      heroCardSpacing: 16,
      heroPositionOffset: 90,
      heroCardOffset: 300,
      extraPadding: 100,
      borderRadius: theme.borderRadius || 20,
    };
    
    // 首先计算内容高度
    const ctx = canvas.getContext('2d');
    canvas.width = config.width;
    
    // 设置字体以便正确测量文本
    ctx.font = `${theme.titleFontWeight} ${config.titleSize}px ${theme.fontFamily}`;
    
    const contentHeight = calculateContentHeight(actions, gameInfo, config);
    const finalHeight = Math.max(config.minHeight, contentHeight + config.extraPadding);
    
    // 设置Canvas尺寸
    canvas.width = config.width;
    canvas.height = finalHeight;
    
    // 创建渐变背景
    if (theme.background.includes('gradient')) {
      const gradient = ctx.createLinearGradient(0, 0, config.width, finalHeight);
      const colors = theme.background.match(/hsla\([^)]+\)/g);
      if (colors) {
        colors.forEach((color, index) => {
          gradient.addColorStop(index / (colors.length - 1), color);
        });
      }
      ctx.fillStyle = gradient;
    } else {
      ctx.fillStyle = theme.background;
    }
    
    roundRect(ctx, 0, 0, config.width, finalHeight, config.borderRadius, true, false);
    
    // 使用传入的翻译函数
    renderContent(ctx, actions, gameInfo, heroInfo, boardCards, config, theme, cardImages, t);
    
    // 导出为PNG
    const dataUrl = canvas.toDataURL('image/png');
    
    // 清理
    document.body.removeChild(container);
    
    return dataUrl;
  } catch (error) {
    // 确保清理
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
    console.error('生成图片时出错:', error);
    throw error;
  }
};

/**
 * 导出Canvas生成的图片
 * @param {Object} options - 图片生成选项
 * @param {string} filename - 下载文件名
 * @param {boolean} autoDownload - 是否自动下载图片（默认为true）
 * @returns {Promise<string>} 生成的图片数据URL
 */
export const exportCanvasImage = async (options, filename = 'hand-history.png', autoDownload = true) => {
  try {
    const { actions, gameInfo, heroInfo, boardCards, isMobile, theme: themeKey = 'light', t } = options;
    
    if (!t || typeof t !== 'function') {
      throw new Error('缺少翻译函数，无法导出图片');
    }
    
    // Use the theme directly from options or fallback to light theme
    const theme = typeof themeKey === 'string' ? themes[themeKey] || themes.light : themeKey;
    
    // Create a temporary container for rendering
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    document.body.appendChild(container);

    // Create a new instance of CanvasCardRenderer with the specified theme
    const renderer = document.createElement('div');
    renderer.style.width = '800px';
    renderer.style.height = '600px';
    container.appendChild(renderer);

    // Render the component with the specified theme
    ReactDOM.render(
      <CanvasCardRenderer
        actions={actions}
        gameInfo={gameInfo}
        heroInfo={heroInfo}
        boardCards={boardCards}
        isMobile={isMobile}
        theme={theme}
        t={t}
      />,
      renderer
    );

    // Generate the image with the current theme and translation function
    const dataUrl = await generateCanvasImage({...options, theme, t});

    // Clean up the temporary container
    document.body.removeChild(container);

    // If autoDownload is true, automatically download the image
    if (autoDownload) {
      // Create a download link
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    return dataUrl;
  } catch (error) {
    console.error('导出扑克牌历史记录卡片图片时出错:', error);
    alert(`导出图片失败: ${error.message || '未知错误'}`);
    throw error;
  }
};

export default CanvasCardRenderer; 